/**
 * The SQL of the three panels that read the analytics database (D521; doc 20 §4.3, §5 row a5): the store's dashboard, the desk's
 * company indicators and the Admin period dashboard. Pure: it renders parameterized text and names the row each statement returns; the
 * API runs it in ONE read-only transaction as `analytics_reader` and folds the rows into the (unchanged) response contracts.
 *
 * WHY HERE. Every statement that carries a tenant lives in this one file, so the question "can a caller reach another tenant's rows?"
 * has one place to be answered. The rules, in code:
 *
 *  - A partner panel's builder takes a {@link PartnerTenant} and NOTHING else that names a tenant: there is no overload that accepts a
 *    market, merchant or account id from a request. The tenant's id and markets are bound parameters `$1`/`$2`; the read scope the API
 *    opens (`withAnalyticsMarkets`) is the same markets, so the database's row level security enforces them a second time.
 *  - No statement selects a customer, driver, courier, order id, name or contact. The cohort statement returns a COUNT of the capped
 *    pseudonymous subject set per bucket (the k-anonymity input) — never the keys — and the API never puts it on the wire.
 *  - Aggregates (`agg_*_hour`, `agg_merchant_cohort_day`) answer the figures they carry; facts answer only what an aggregate does not
 *    hold (the status split, the four-way outcome, the charge count per currency, the ride assignment split, the sold lines).
 *    Both are rebuilt by the same ETL run, so an hour is never in one and missing from the other for longer than a run.
 *
 * Dates are the market's (or, for the desk, the account's billing-zone) LOCAL days: `date_key` is exactly "whole local days", so no
 * instant arithmetic is needed for the merchant and the desk. The ops period is two UTC instants and is cut on `requested_at` /
 * `posted_at` exactly (the `date_key` bounds only prune partitions).
 */
import { SEMANTIC_VOCABULARY } from './catalog.js';
const K = SEMANTIC_VOCABULARY.sourceKind;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const date = (v) => {
    if (!DATE.test(v))
        throw new RangeError('a panel date is YYYY-MM-DD');
    return v;
};
/**
 * The age of the data (review P3-1): the OLDEST `extract` watermark among the caller's scoped markets that have one, from
 * `analytics.market_freshness()` (a0007; it returns the ETL's run time for the caller's own markets only). It is no longer the newest
 * aggregate stamp: that was market-wide (a side channel on other tenants' activity) and read stale for a quiet market.
 */
export const FRESHNESS_SQL = `SELECT min(extracted_through)::text AS newest FROM analytics.market_freshness()`;
/** The markets are the transaction's own scope (the function reads it); nothing is passed. */
export const freshnessQuery = (_marketIds) => ({ sql: FRESHNESS_SQL, params: [] });
const MERCHANT_KEYS = `(SELECT m.merchant_key FROM analytics.dim_merchant m WHERE m.market_id = $1 AND m.natural_key = $2)`;
/** The statements of one store dashboard. `$1` = the tenant's market, `$2` = the tenant's merchant; nothing else names a tenant. */
export function merchantPanelQueries(tenant, w) {
    const base = [tenant.marketId, tenant.merchantRef];
    const from = date(w.from);
    const to = date(w.to);
    const rangeFrom = w.prevFrom === null ? from : date(w.prevFrom);
    const win = (col) => `CASE WHEN ${col} >= $3::date THEN 'cur' ELSE 'prev' END`;
    return {
        markets: [tenant.marketId],
        market: { sql: `SELECT timezone, currency::text AS currency FROM analytics.dim_market WHERE market_id = $1`, params: [tenant.marketId] },
        orders: {
            sql: `SELECT ${win('fo.date_key')} AS win, fo.status, count(*)::int AS n,
       (count(*) FILTER (WHERE fo.status = 'REJECTED' AND cr.actor_kind = 'SYSTEM'))::int AS auto_refused,
       (count(*) FILTER (WHERE fo.status = 'CANCELLED' AND fo.cancel_reason_key IS NOT NULL))::int AS cancelled_with_reason,
       max(fo.source_updated_at) AS newest
  FROM analytics.fact_order fo
  LEFT JOIN analytics.dim_cancel_reason cr ON cr.market_id = fo.market_id AND cr.cancel_reason_key = fo.cancel_reason_key
 WHERE fo.market_id = $1 AND fo.vertical = 'FOOD' AND fo.merchant_key IN ${MERCHANT_KEYS}
   AND fo.date_key BETWEEN $4::date AND $5::date
 GROUP BY 1, 2`,
            params: [...base, from, rangeFrom, to],
        },
        daily: {
            sql: `SELECT a.date_key::text AS day, a.currency::text AS currency, sum(a.orders_count)::int AS orders, sum(a.completed_count)::int AS completed,
       sum(a.gross_minor)::text AS gross, sum(a.merchandise_minor)::text AS merchandise, sum(a.delivery_minor)::text AS delivery,
       sum(a.discount_minor)::text AS discount, sum(a.commission_minor)::text AS commission, sum(a.partner_net_minor)::text AS net
  FROM analytics.agg_merchant_hour a
 WHERE a.market_id = $1 AND a.merchant_ref = $2 AND a.date_key BETWEEN $3::date AND $4::date
 GROUP BY 1, 2`,
            params: [...base, rangeFrom, to],
        },
        bookings: {
            sql: `SELECT ${win('fm.date_key')} AS win, fm.currency::text AS currency, count(*)::int AS n, min(fm.posted_at) AS first_at, max(fm.source_updated_at) AS newest
  FROM analytics.fact_money fm
 WHERE fm.market_id = $1 AND fm.source_kind = '${K.orderBooking}' AND fm.merchant_key IN ${MERCHANT_KEYS}
   AND fm.date_key BETWEEN $4::date AND $5::date
 GROUP BY 1, 2`,
            params: [...base, from, rangeFrom, to],
        },
        cohorts: {
            sql: `WITH c AS (
  SELECT dimension, bucket, currency::text AS currency, orders_count, amount_minor, subjects
    FROM analytics.agg_merchant_cohort_day
   WHERE market_id = $1 AND merchant_ref = $2 AND date_key BETWEEN $3::date AND $4::date),
 t AS (SELECT dimension, bucket, currency, sum(orders_count)::int AS orders, sum(amount_minor)::text AS amount FROM c GROUP BY 1, 2, 3),
 s AS (SELECT dimension, bucket, currency, count(DISTINCT x)::int AS people FROM c, unnest(c.subjects) AS x GROUP BY 1, 2, 3)
SELECT t.dimension, t.bucket, t.currency, t.orders, t.amount, COALESCE(s.people, 0)::int AS people
  FROM t LEFT JOIN s USING (dimension, bucket, currency)`,
            params: [...base, from, to],
        },
        items: {
            // Only the DELIVERED orders' lines, the category AT THE TIME OF SALE (the item version in force), named only when the ETL found it
            // owned by this merchant (an unowned or another's item carries no category in the star).
            // The lines are summed per (item version, category, currency) FIRST - on the fact's own keys, with the same date range and merchant on
            // the order side so its partitions are pruned - and only then named through the dimensions.
            sql: `WITH d AS MATERIALIZED (
  SELECT fo.order_id, fo.date_key FROM analytics.fact_order fo
   WHERE fo.market_id = $1 AND fo.vertical = 'FOOD' AND fo.merchant_key IN ${MERCHANT_KEYS} AND fo.date_key BETWEEN $3::date AND $4::date AND fo.status = 'DELIVERED'),
 s AS (
  SELECT fi.item_key, fi.category_key, fi.currency, sum(fi.quantity) AS quantity, sum(fi.amount_minor) AS amount
    FROM analytics.fact_order_item fi
    JOIN d ON d.order_id = fi.order_id AND d.date_key = fi.date_key
   WHERE fi.market_id = $1 AND fi.vertical = 'FOOD' AND fi.merchant_key IN ${MERCHANT_KEYS} AND fi.date_key BETWEEN $3::date AND $4::date
   GROUP BY 1, 2, 3)
SELECT di.natural_key AS item_ref,
       -- the catalog name only for THIS store's own item; any other item reads by its reference (its star name may have been learned from another store)
       (array_agg(di.display_name ORDER BY di.valid_from DESC) FILTER (WHERE di.merchant_natural_key = $2))[1] AS item_name, max(di.valid_from) AS item_valid_from,
       dc.natural_key AS category_ref, dc.display_name AS category_name, s.currency::text AS currency,
       sum(s.quantity)::int AS quantity, sum(s.amount)::text AS amount
  FROM s
  LEFT JOIN analytics.dim_item di ON di.market_id = $1 AND di.item_key = s.item_key
  LEFT JOIN analytics.dim_category dc ON dc.market_id = $1 AND dc.category_key = s.category_key
 GROUP BY di.natural_key, dc.natural_key, dc.display_name, s.currency`,
            params: [...base, from, to],
        },
    };
}
/**
 * The statements of one desk's indicators. `$1` = the account's markets, `$2` = the account; `$5` is the account's CURRENT billing zone, and the
 * days are cut from the facts' instants with `AT TIME ZONE` - so a zone that changed after the hours were aggregated (the hourly aggregate keeps the
 * zone it was cut in until the rebuild) never refuses or misplaces a day (review P2-1). An account's rows are few (it is one company's desk), so
 * two grouped scans of its partition-pruned rows (the `date_key` bounds only prune) are cheap; `agg_partner_hour` is not on this path.
 */
export function partnerPanelQueries(tenant, p) {
    const from = date(p.from);
    const to = date(p.to);
    const base = [[...tenant.marketIds], tenant.accountRef, from, to, p.timeZone];
    const account = `JOIN analytics.dim_partner_account pa ON pa.market_id = f.market_id AND pa.partner_account_key = f.partner_account_key AND pa.natural_key = $2`;
    return {
        markets: tenant.marketIds,
        orders: {
            sql: `SELECT ((f.requested_at AT TIME ZONE $5::text)::date)::text AS day, f.vertical, f.status, mk.currency::text AS currency, count(*)::int AS n
  FROM analytics.fact_order f ${account}
  JOIN analytics.dim_market mk ON mk.market_id = f.market_id
 WHERE f.market_id = ANY($1::text[]) AND f.date_key BETWEEN $3::date - 2 AND $4::date + 2
   AND (f.requested_at AT TIME ZONE $5::text)::date BETWEEN $3::date AND $4::date
 GROUP BY 1, 2, 3, 4`,
            params: base,
        },
        charges: {
            sql: `SELECT ((f.posted_at AT TIME ZONE $5::text)::date)::text AS day, f.vertical, f.currency::text AS currency, count(*)::int AS n, sum(f.amount_minor)::text AS spend
  FROM analytics.fact_money f ${account}
 WHERE f.market_id = ANY($1::text[]) AND f.source_kind IN ('${K.partnerChargeAuthorized}', '${K.partnerChargeSettled}') AND f.vertical IS NOT NULL
   AND f.date_key BETWEEN $3::date - 2 AND $4::date + 2
   AND (f.posted_at AT TIME ZONE $5::text)::date BETWEEN $3::date AND $4::date
 GROUP BY 1, 2, 3`,
            params: base,
        },
    };
}
const utcInstant = (v) => {
    const t = Date.parse(v);
    if (!Number.isFinite(t))
        throw new RangeError('an ops period is two UTC instants');
    return new Date(t).toISOString();
};
/**
 * The statements of the ops period for ONE market (the caller loops the markets its authorization reached, each in its own scope). The
 * market comes from the server's authorization, never from a row; the `date_key` bounds (one day each side of the UTC dates covers every
 * zone) only prune partitions, the instants cut exactly.
 */
export function opsPeriodQueries(marketId, period) {
    const from = utcInstant(period.from);
    const to = utcInstant(period.to);
    const params = [marketId, from, to];
    const ride = `f.market_id = $1 AND f.vertical = 'RIDE' AND f.date_key BETWEEN ($2::timestamptz AT TIME ZONE 'UTC')::date - 1 AND ($3::timestamptz AT TIME ZONE 'UTC')::date + 1
   AND f.requested_at >= $2::timestamptz AND f.requested_at < $3::timestamptz`;
    return {
        markets: [marketId],
        counts: {
            sql: `SELECT count(*)::int AS requested,
       (count(*) FILTER (WHERE f.driver_key IS NOT NULL))::int AS assigned,
       (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
       (count(*) FILTER (WHERE f.status = 'CANCELLED' AND f.driver_key IS NULL))::int AS cancelled_before,
       (count(*) FILTER (WHERE f.status = 'CANCELLED' AND f.driver_key IS NOT NULL))::int AS cancelled_after,
       (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.noDriver}'))::int AS no_driver
  FROM analytics.fact_order f WHERE ${ride}`,
            params,
        },
        days: {
            sql: `SELECT f.date_key::text AS day, count(*)::int AS requested, (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
       (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.noDriver}'))::int AS no_driver
  FROM analytics.fact_order f WHERE ${ride} GROUP BY 1 ORDER BY 1`,
            params,
        },
        revenue: {
            sql: `SELECT f.currency::text AS currency, count(*)::int AS rides, sum(f.gross_minor)::text AS due, sum(f.amount_minor)::text AS received, sum(f.commission_minor)::text AS platform
  FROM analytics.fact_money f
 WHERE f.market_id = $1 AND f.source_kind IN ('${K.rideCash}', '${K.rideSettlement}')
   AND f.date_key BETWEEN ($2::timestamptz AT TIME ZONE 'UTC')::date - 1 AND ($3::timestamptz AT TIME ZONE 'UTC')::date + 1
   AND f.posted_at >= $2::timestamptz AND f.posted_at < $3::timestamptz
 GROUP BY 1`,
            params,
        },
    };
}
/**
 * The hourly statements of ONE market over two UTC instants (D559). `hours` reads the ride fact, grouped by the market-local
 * `(date_key, hour_key, hour_occurrence)` the ETL stamped, cut exactly on `requested_at` like the period `counts`/`days` (so the sum of
 * the hours is the headline figure). It is the fact and not `agg_market_hour` because the aggregate carries no `NO_DRIVER` outcome and its
 * grain is whole hours while the window is two instants; the plan is the same `fact_order_market_date` range the `days` statement uses.
 * `presence` reads the operator view `ops_presence_market_hour` (a0017: the market x hour sum, NO driver in it and NO privacy floor - the owner's
 * decision of 2026-10-12 for the authenticated, market-scoped operator dashboard; `agg_presence_market_hour` keeps its floor of five for everything
 * else) over the dates the window touches (one day either side, the caller keeps the buckets it needs). `coverage` says which 15-minute stretches
 * of the window the sampler actually recorded (`presence_slot`, its slot length divides 900 s so a slot never straddles two stretches): an hour
 * with coverage and no presence row is a TRUE zero, an hour without coverage has no presence data at all (the caller reads `null`, not 0).
 */
export function opsHourlyQueries(marketId, period) {
    const from = utcInstant(period.from);
    const to = utcInstant(period.to);
    const params = [marketId, from, to];
    const dates = `f.date_key BETWEEN ($2::timestamptz AT TIME ZONE 'UTC')::date - 1 AND ($3::timestamptz AT TIME ZONE 'UTC')::date + 1`;
    return {
        markets: [marketId],
        hours: {
            sql: `SELECT f.date_key::text AS day, f.hour_key::int AS hour, f.hour_occurrence::int AS occurrence, count(*)::int AS requested,
       (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
       (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.noDriver}'))::int AS no_driver,
       (count(*) FILTER (WHERE f.status = 'CANCELLED'))::int AS cancelled
  FROM analytics.fact_order f
 WHERE f.market_id = $1 AND f.vertical = 'RIDE' AND ${dates}
   AND f.requested_at >= $2::timestamptz AND f.requested_at < $3::timestamptz
 GROUP BY 1, 2, 3 ORDER BY 1, 2, 3`,
            params,
        },
        presence: {
            sql: `SELECT f.date_key::text AS day, f.hour_key::int AS hour, f.hour_occurrence::int AS occurrence, f.drivers_online::int AS drivers_online
  FROM analytics.ops_presence_market_hour f
 WHERE f.market_id = $1 AND ${dates}
 ORDER BY 1, 2, 3`,
            params,
        },
        coverage: {
            sql: `SELECT (floor(extract(epoch FROM s.slot_start) / 900) * 900)::bigint AS epoch, count(*)::int AS slots
  FROM analytics.presence_slot s
 WHERE s.market_id = $1 AND s.slot_start >= $2::timestamptz AND s.slot_start < $3::timestamptz
 GROUP BY 1 ORDER BY 1`,
            params,
        },
    };
}
//# sourceMappingURL=panels.js.map